






























































































































































import { Component, Prop, Watch, Vue } from "vue-property-decorator";

import { endpoints } from "@/endpoints";
import { eventLoading } from "@/components/sce/EventLoading";
import {
    fmtFloat,
    fmtHours,
    fmtKmDays,
    fmtInt,
    fmtKm,
    fmtPct,
    fmtTonnes,
} from "./formatters";
import { buildRcaUrl, RcaFilters } from "./filterQueryParams";
import ScenarioWindowFootnote from "./ScenarioWindowFootnote.vue";

const mapboxgl = require("mapbox-gl");

/** HTML-escape a value for safe interpolation into popup markup. */
const escapeHtml = (s: any): string =>
    String(s ?? "").replace(/[&<>"']/g, (c) => {
        switch (c) {
            case "&":
                return "&amp;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            case '"':
                return "&quot;";
            default:
                return "&#39;";
        }
    });

interface PanelState {
    loading: boolean;
    error: string | null;
    data: any[];
}

function newPanelState(): PanelState {
    return { loading: false, error: null, data: [] };
}

interface MetricOption {
    key: string;
    label: string;
    fmt: (v: any) => string;
    /** One-line note shown in the hover popup explaining what the metric counts (closed road
     * segments, trips, or enterprises) and which LGA it is attributed to — so users know whether
     * the value is about things located inside this LGA or things elsewhere tied to it. */
    basis?: string;
}

/** Attribution notes surfaced in the hover popup. */
const BASIS_CLOSURE = "Counts closed road segments located in this LGA.";
const BASIS_TRIP =
    "Counts trips affected by a closure located in this LGA — the trip's origin/destination may be outside this LGA.";
const BASIS_ENTERPRISE =
    "Counts enterprises located in this LGA — the closure that affected them may be in another LGA.";

const METRIC_OPTIONS: MetricOption[] = [
    {
        key: "closure_event_count_sum",
        label: "Recorded road closure events",
        fmt: (v) => fmtInt(v),
        basis: BASIS_CLOSURE,
    },
    {
        key: "closed_link_count_sum",
        label: "Closed segment-days",
        fmt: (v) => fmtInt(v),
        basis: BASIS_CLOSURE,
    },
    {
        key: "closed_length_km_sum",
        label: "Closed-segment km-days",
        fmt: fmtKmDays,
        basis: BASIS_CLOSURE,
    },
    {
        key: "active_day_count",
        label: "Closure duration (days)",
        fmt: (v) => fmtInt(v),
        basis: BASIS_CLOSURE,
    },
    {
        key: "active_day_share",
        label: "Share of days closed",
        fmt: (v) => fmtPct(v),
        basis: BASIS_CLOSURE,
    },
    {
        key: "blocked_exposed_tonnes",
        label: "Blocked tonnes",
        fmt: fmtTonnes,
        basis: BASIS_TRIP,
    },
    {
        key: "rerouted_exposed_tonnes",
        label: "Rerouted tonnes",
        fmt: fmtTonnes,
        basis: BASIS_TRIP,
    },
    {
        key: "net_additional_travel_km",
        label: "Total extra transport kilometres *",
        fmt: fmtKm,
        basis: BASIS_TRIP,
    },
    {
        key: "net_additional_travel_hours",
        label: "Total extra transport hours *",
        fmt: fmtHours,
        basis: BASIS_TRIP,
    },
    {
        key: "blocked_enterprise_day_count",
        label: "Blocked enterprise-days",
        fmt: (v) => fmtInt(v),
        basis: BASIS_ENTERPRISE,
    },
    {
        key: "rerouted_enterprise_day_count",
        label: "Rerouted enterprise-days",
        fmt: (v) => fmtInt(v),
        basis: BASIS_ENTERPRISE,
    },
];

// TEMPORARY: the four tonnes/travel LGA metrics are turned off until the
// pre-computed summary lands - they currently come back as 0. Hide them from the metric
// dropdown and the table.
const TONNES_TRAVEL_METRICS_ENABLED = false;
const TONNES_TRAVEL_KEYS = new Set([
    "blocked_exposed_tonnes",
    "rerouted_exposed_tonnes",
    "net_additional_travel_km",
    "net_additional_travel_hours",
]);

/** Hard cap on the LGA exposure fetch. Australia has ~537 LGAs, so 1000 is effectively
 * unlimited. */
const LGA_FETCH_LIMIT = 1000;

/** Fill-layer + outline IDs we add to the map. */
const LGA_FILL_LAYER_ID = "rca-lga-fill";
const LGA_LINE_LAYER_ID = "rca-lga-line";
const LGA_HOVER_LAYER_ID = "rca-lga-hover";
/** Persistent outline drawn on the currently-focused LGA (table-row click or map click).*/
const LGA_FOCUS_LAYER_ID = "rca-lga-focus";
const BOUNDARIES_SOURCE_ID = "rca-boundaries-source";
const LGA_SOURCE_LAYER = "LGA";

/** TEMPORARY WORKAROUND — DELETE WHEN PROTOTYPE IS UPDATED.
 *
 * The shared ``mv_transit_boundaries`` Postgres table (the single source of truth for
 * LGA / SA2 / SA3 / etc. polygons across the whole TraNSIT app) stores LGA codes
 * prefixed with ``LGA_`` (e.g. ``LGA_10250``). The road-closure prototype's DuckDB
 * stores them as 5-digit strings (``"10250"``) on the ``lga_code23`` column. We
 * prepend this prefix wherever we join an exposure row's ``lga_code23`` to a boundary
 * tile's ``code``.
 *
 * This belongs at the data layer, not the UI. The prototype's bootstrap script should
 * write ``LGA_<code>`` into ``lga_code23`` so the value joins directly against
 * ``mv_transit_boundaries.code``. Once that change lands, drop this constant and revert
 * every call site that references it.
 *
 * Tracked in the prototype at ``road_closure_analytics_schema.sql`` (column docstring)
 * and ``bootstrap_road_closure_analytics.py`` (where the value is read from the
 * boundary parquet). */

/* NOTE: TraNSIT Web is still using the 2016 LGA boundaries dataset. The bootstrap road analytics prototype is
   using LGA 2023 dataset. A team decision will need to be made on what dataset to use.*/

const BOUNDARY_LGA_CODE_PREFIX = "LGA_";

@Component({ components: { ScenarioWindowFootnote } })
export default class GeographyTab extends Vue {
    @Prop({ required: true }) startDate!: string;
    @Prop({ required: true }) endDate!: string;
    /** Global sidebar filters — narrow LGA exposure to matching trips. */
    @Prop({ default: () => ({}) }) filters!: RcaFilters;
    /** Whether this tab is currently the active one. Mapbox-GL initialises with the
     * container's bounding rect, so when the tab is hidden at mount time it ends up at
     * 0×0; we call ``map.resize()`` whenever this becomes true. */
    @Prop({ default: true }) active!: boolean;

    lga: PanelState = newPanelState();

    fmtFloat = fmtFloat;
    fmtPct = fmtPct;
    fmtInt = fmtInt;

    /** Selected choropleth metric. */
    metric: string = "closure_event_count_sum";
    metricOptions = TONNES_TRAVEL_METRICS_ENABLED
        ? METRIC_OPTIONS
        : METRIC_OPTIONS.filter((m) => !TONNES_TRAVEL_KEYS.has(m.key));

    /** Currently-focused LGA, stored as the raw ``lga_code23`` string. Drives the map highlight + focus badge and bolds the matching table row.
     * Null when nothing is focused. */
    selectedLgaCode: string | null = null;
    /** When the focus came from a map click, float the selected row to the top of the table
     * (so a click on a map polygon surfaces the row even if it ranks low).
     * Table-row clicks leave row order untouched. */
    bringSelectedToTop = false;

    // mapbox-gl plumbing
    mapboxAccessToken =
        "pk.eyJ1IjoiYm9uMTMyIiwiYSI6ImNqdXRhYmw1OTA1eGUzeW5yZGo3OWZmankifQ.RYbaSeGdz3Nq_hIGWXrpSw";
    mapStyleUrl = "mapbox://styles/bon132/cmm8gavux000101sjauuwfvpe";
    /** Australian-wide initial view. mapbox-gl will fitBounds once we know which LGAs
     * have data, but this is the safe fallback. */
    initialCenter: [number, number] = [134, -28];
    initialZoom = 3.8;

    /** mapbox-gl map instance + popup. Both lazily created in mounted(). */
    private map: any = null;
    private popup: any = null;
    /** Watches the map container for size changes (tab activation, browser resize,
     * sidebar collapse) and tells mapbox-gl to recompute its dimensions. Without this
     * the map renders to whatever size the container had at first paint and stays there.*/
    private resizeObserver: any = null;
    /** The dataset slug used in the boundaries tile URL. The boundaries SQL doesn't
     * reference dataset for filtering — it's a routing/cache key — so any registered
     * dataset works. We pull the first one from the /datasets endpoint, mirroring the
     * SCE store's default-pick logic. */
    private datasetSlug: string | null = null;
    boundaryError: string | null = null;

    /** The LGA ``code`` currently carrying ``hover`` feature-state, so we can clear it before
     * highlighting the next one (keeps exactly one LGA highlighted at a time). */
    private hoveredCode: string | null = null;

    /** Each LGA's bounding box, keyed by the prefixed boundary ``code`` (``LGA_<code>``), stored
     * as the ``[[west, south], [east, north]]`` shape Mapbox ``fitBounds`` accepts. Fetched once
     * from the ``lga_bounds`` endpoint (authoritative extents from the Postgres boundaries
     * table), so a row / map click can fly straight to the LGA. LGA polygons are static, so this
     * survives date-range / filter changes. */
    private lgaBoundsCache: Map<string, [[number, number], [number, number]]> =
        new Map();

    mounted() {
        this.fetchAll();
        this.initMap();
    }

    beforeDestroy() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
            this.resizeObserver = null;
        }
        if (this.popup) {
            this.popup.remove();
            this.popup = null;
        }
        if (this.map) {
            this.map.remove();
            this.map = null;
        }
    }

    /** One key over every fetch input so a param change triggers exactly one fetch. */
    private get fetchParamsKey(): string {
        return `${this.startDate}|${this.endDate}|${JSON.stringify(
            this.filters,
        )}`;
    }

    @Watch("fetchParamsKey")
    onParamsChanged() {
        this.fetchAll();
    }

    @Watch("lgaRows")
    @Watch("metric")
    onChoroplethInputsChanged() {
        this.applyChoroplethStyling();
    }

    @Watch("active")
    onActiveChanged(now: boolean) {
        if (!now || !this.map) return;
        // Two-step resize after activation: first rAF lets the v-tab-item finish its
        // display: none -> block transition, then a short setTimeout covers any browser
        // layout that's still settling. Without this the map stays at its last-known
        // (possibly zero) size from when the tab was hidden.
        requestAnimationFrame(() => {
            window.setTimeout(() => {
                if (this.map) this.map.resize();
            }, 80);
        });
    }

    fetchAll() {
        if (!this.startDate || !this.endDate) return;
        this.selectedLgaCode = null;
        this.bringSelectedToTop = false;
        this.applyFocusHighlight();
        this.fetchPanel(
            this.lga,
            endpoints.roadClosureAnalyticsLgaExposureUrl,
            { limit: LGA_FETCH_LIMIT },
        );
    }

    fetchPanel(panel: PanelState, url: string, extra: Record<string, any>) {
        panel.loading = true;
        panel.error = null;
        eventLoading.startLoading();
        Vue.axios({
            url: buildRcaUrl(
                url,
                {
                    start_date: this.startDate,
                    end_date: this.endDate,
                    ...extra,
                },
                this.filters,
            ),
        })
            .then((response: any) => {
                panel.data = response.data || [];
            })
            .catch((err: any) => {
                console.error("[RCA Geography]", err);
                panel.error = err?.message || "Failed to load.";
                panel.data = [];
            })
            .finally(() => {
                panel.loading = false;
                eventLoading.stopLoading();
            });
    }

    /**
     * Lazy-init the Mapbox-GL map. Walks the bootstrap in three stages so a slow boundary
     * tile fetch (or a missing dataset) doesn't block the rest of the tab from rendering.
     */
    async initMap() {
        await this.$nextTick();
        const container = this.$refs.mapContainer as HTMLElement | undefined;
        if (!container) return;
        try {
            mapboxgl.accessToken = this.mapboxAccessToken;
            this.map = new mapboxgl.Map({
                container,
                style: this.mapStyleUrl,
                center: this.initialCenter,
                zoom: this.initialZoom,
                attributionControl: false,
                // Same auth-injection trick the SCE TransitMap uses: every tile request
                // that isn't going to mapbox.com gets the Authorization + X-Username
                // headers from the auth store. Without this the boundaries-source tile
                // requests come back 401 because they hit our Django backend.
                transformRequest: this.addAuthHeaders,
            });
            this.map.addControl(new mapboxgl.NavigationControl(), "top-right");
            this.map.on("load", async () => {
                try {
                    await this.ensureBoundariesSource();
                    this.addLgaLayers();
                    this.applyChoroplethStyling();
                    this.attachHoverHandlers();
                    // Once layers are in, force a resize in case the container's size
                    // changed between map init and now (common when the tab wasn't the
                    // initial one — see onActiveChanged).
                    if (this.map) this.map.resize();
                } catch (err) {
                    const e = err as any;
                    console.error("[RCA Geography map]", e);
                    this.boundaryError =
                        e?.message || "Could not load LGA boundaries.";
                }
            });
            // Catch every other resize event the parent column throws at us (sidebar
            // collapse, browser zoom, viewport rotate). Cheaper and more reliable than
            // listening to window resize, since this fires on any container size change.
            // ``ResizeObserver`` isn't in the TS 3.8 lib so we access via window.
            const ROCtor = (window as any).ResizeObserver;
            if (typeof ROCtor !== "undefined") {
                this.resizeObserver = new ROCtor(() => {
                    if (this.map) this.map.resize();
                });
                this.resizeObserver.observe(container);
            }
        } catch (err) {
            const e = err as any;
            console.error("[RCA Geography mapbox-gl]", e);
            this.boundaryError = e?.message || "Could not initialise map.";
        }
    }

    /**
     * Mapbox-GL transformRequest hook. Mapbox issues tile / sprite / glyph fetches via the
     * browser's native fetch (NOT through Vue.axios), so it bypasses any axios interceptors
     * that normally attach our auth headers. We inject the same Authorization + X-Username
     * pair the SCE TransitMap uses for every non-mapbox.com URL — that covers our own
     * boundaries-source endpoint while leaving Mapbox's CDN requests untouched.
     */
    addAuthHeaders(url: string, resourceType: string) {
        if (!url || url.includes("mapbox.com")) return { url };
        // Match TransitMap.vue's addJWTToken (lines 2260-2262): mapbox-gl loads vector
        // tiles in a Web Worker, which has no document.baseURI, so a relative URL like
        // "/api/tiles/..." fails when the Worker calls `new Request(...)`. Prepend the
        // page origin when the URL is relative; absolute URLs are left untouched.
        if (url.startsWith("/")) {
            url = window.location.origin + url;
        }
        const auth: any = (this.$store as any)?.state?.auth ?? {};
        const headers: Record<string, string> = {};
        if (auth.access_token) {
            headers["Authorization"] = "Bearer " + auth.access_token;
        }
        if (auth.user?.username) {
            headers["X-Username"] = auth.user.username;
        }
        return { url, headers };
    }

    /**
     * Resolve a dataset slug (any registered one works since the boundaries SQL ignores
     * it for filtering) and add the boundaries-source vector tileset. Cached after the
     * first successful resolve so subsequent re-mounts are instant.
     */
    async ensureBoundariesSource(): Promise<void> {
        if (!this.datasetSlug) {
            const response: any = await Vue.axios({
                url: endpoints.datasetsUrl,
            });
            const list = Array.isArray(response.data) ? response.data : [];
            const first = list[0]?.ID;
            if (!first) {
                throw new Error(
                    "No dataset registered on the backend; LGA boundaries " +
                        "endpoint requires a dataset slug.",
                );
            }
            this.datasetSlug = String(first);
        }
        // Prefetch the per-LGA bounding boxes (static) so zoom-to-LGA can fly straight to a
        // clicked LGA without scraping geometry out of the rendered tiles.
        this.fetchLgaBounds();
        if (this.map.getSource(BOUNDARIES_SOURCE_ID)) return;
        this.map.addSource(BOUNDARIES_SOURCE_ID, {
            type: "vector",
            // Unconstrained boundaries
            tiles: [
                endpoints.roadClosureAnalyticsBoundariesTileUrlTemplate(
                    this.datasetSlug,
                ),
            ],
            minzoom: 0,
            maxzoom: 18,
            // Promote the per-feature `code` property to the feature ID so we can use
            // mapbox feature-state lookups against it (handy for the hover layer).
            promoteId: "code",
        });
    }

    addLgaLayers() {
        // Slot all LGA layers BELOW the first symbol layer (city / suburb labels), so the
        // base style's place names render on top of our choropleth — matches how the SCE
        // map composes its boundary fills and keeps the labels readable.
        const firstSymbolId = this.firstSymbolLayerId();
        // Fill — coloured by metric value via a paint expression we recompute when data
        // or the chosen metric changes. Initial fallback colour until applyChoropleth runs.
        this.map.addLayer(
            {
                id: LGA_FILL_LAYER_ID,
                type: "fill",
                source: BOUNDARIES_SOURCE_ID,
                "source-layer": LGA_SOURCE_LAYER,
                paint: {
                    "fill-color": "#e8e8e8",
                    "fill-opacity": 0.65,
                },
            },
            firstSymbolId,
        );
        // Outline — keeps LGA borders visible even when fill colour barely contrasts.
        this.map.addLayer(
            {
                id: LGA_LINE_LAYER_ID,
                type: "line",
                source: BOUNDARIES_SOURCE_ID,
                "source-layer": LGA_SOURCE_LAYER,
                paint: {
                    "line-color": "rgba(1, 62, 117, 0.45)",
                    "line-width": 0.4,
                },
            },
            firstSymbolId,
        );
        // Hover overlay — dark line, only visible on the hovered LGA.
        this.map.addLayer(
            {
                id: LGA_HOVER_LAYER_ID,
                type: "line",
                source: BOUNDARIES_SOURCE_ID,
                "source-layer": LGA_SOURCE_LAYER,
                paint: {
                    "line-color": "#013e75",
                    "line-width": [
                        "case",
                        ["boolean", ["feature-state", "hover"], false],
                        2.5,
                        0,
                    ],
                    "line-width-transition": { duration: 0, delay: 0 },
                    "line-opacity": [
                        "case",
                        ["boolean", ["feature-state", "hover"], false],
                        1,
                        0,
                    ],
                    "line-opacity-transition": { duration: 0, delay: 0 },
                },
            },
            firstSymbolId,
        );
        // Focus overlay — a thick, persistent outline on the LGA the user selected (from the
        // table or by clicking the map).
        this.map.addLayer(
            {
                id: LGA_FOCUS_LAYER_ID,
                type: "line",
                source: BOUNDARIES_SOURCE_ID,
                "source-layer": LGA_SOURCE_LAYER,
                paint: {
                    "line-color": "#ff6f00",
                    "line-width": 4,
                },
                filter: ["==", ["get", "code"], "__none__"],
            },
            firstSymbolId,
        );
        // Re-assert any focus that was set before the layers existed (e.g. a click that
        // landed during map load).
        this.applyFocusHighlight();
    }

    /**
     * Find the id of the first symbol-type layer in the active style. Mapbox-GL renders
     * layers bottom-to-top in the order they appear in the style, so inserting our LGA
     * fills BEFORE the first symbol layer puts text labels (cities, suburbs, road names)
     * back on top of our choropleth where users expect them.
     */
    firstSymbolLayerId(): string | undefined {
        const layers = this.map?.getStyle()?.layers ?? [];
        const first = layers.find((l: any) => l.type === "symbol");
        return first?.id;
    }

    /**
     * Recompute the fill-color paint expression from the current ``lgaRows`` + ``metric``.
     * We build a Mapbox ``match`` expression that looks up each LGA code in our value map
     * and returns a precomputed colour; LGAs not in the map get the no-data fallback.
     */
    applyChoroplethStyling() {
        if (!this.map || !this.map.getLayer(LGA_FILL_LAYER_ID)) return;
        const rows = this.lgaRows;
        const max = this.metricRange.max;
        const fallback = "#e8e8e8";
        if (!rows.length || max <= 0) {
            this.map.setPaintProperty(
                LGA_FILL_LAYER_ID,
                "fill-color",
                fallback,
            );
            return;
        }
        // Sqrt scale — closure activity is heavily skewed (a few LGAs dominate), so a
        // linear scale leaves most polygons looking the same pale colour. Sqrt opens up
        // the bottom of the distribution without over-flattening the top.
        const valueByCode = new Map<string, number>();
        for (const r of rows) {
            const raw = r.lga_code23 != null ? String(r.lga_code23) : null;
            if (!raw) continue;
            // TEMPORARY: prefix until the prototype writes ``LGA_<code>`` natively. See
            // the BOUNDARY_LGA_CODE_PREFIX docstring above for the removal plan.
            valueByCode.set(
                BOUNDARY_LGA_CODE_PREFIX + raw,
                Number(r[this.metric]) || 0,
            );
        }
        const matchPairs: any[] = [];
        valueByCode.forEach((v, code) => {
            matchPairs.push(code, this.colorForValue(v, max));
        });
        if (!matchPairs.length) {
            this.map.setPaintProperty(
                LGA_FILL_LAYER_ID,
                "fill-color",
                fallback,
            );
            return;
        }
        this.map.setPaintProperty(LGA_FILL_LAYER_ID, "fill-color", [
            "match",
            ["get", "code"],
            ...matchPairs,
            fallback,
        ]);
    }

    /**
     * Map a metric value to a colour on a white → indigo → red ramp using a sqrt-scaled
     * normalisation. The first half of the ramp (t<0.5) interpolates white → indigo;
     * the second half (t≥0.5) interpolates indigo → red. Indigo is rgb(63, 81, 181).
     */
    colorForValue(value: number, max: number): string {
        if (!Number.isFinite(value) || value <= 0 || max <= 0) return "#e8e8e8";
        const t = Math.sqrt(value) / Math.sqrt(max);
        const clamped = Math.max(0, Math.min(1, t));
        const lerp = (a: number, b: number, u: number) =>
            Math.round(a + (b - a) * u);
        let r: number, g: number, b: number;
        if (clamped < 0.5) {
            const u = clamped * 2;
            r = lerp(255, 63, u);
            g = lerp(255, 81, u);
            b = lerp(255, 181, u);
        } else {
            const u = (clamped - 0.5) * 2;
            r = lerp(63, 255, u);
            g = lerp(81, 0, u);
            b = lerp(181, 0, u);
        }
        return `rgb(${r}, ${g}, ${b})`;
    }

    /**
     * Wire mouse-move + mouse-leave handlers on the LGA fill so the hover overlay tracks
     * the cursor and a popup shows the LGA name + state + metric value.
     */
    attachHoverHandlers() {
        if (!this.map) return;
        this.popup = new mapboxgl.Popup({
            closeButton: false,
            closeOnClick: false,
            offset: 6,
        });
        this.map.on("mousemove", LGA_FILL_LAYER_ID, (e: any) => {
            const feature = e.features?.[0];
            if (!feature) return;
            this.map.getCanvas().style.cursor = "pointer";
            const code = String(feature.properties?.code ?? feature.id ?? "");
            this.setHoveredLga(code);
            const row = this.rowByCode.get(code);
            const popupHtml = this.buildPopupHtml(feature, row);
            this.popup.setLngLat(e.lngLat).setHTML(popupHtml).addTo(this.map);
        });
        this.map.on("mouseleave", LGA_FILL_LAYER_ID, () => {
            this.map.getCanvas().style.cursor = "";
            this.setHoveredLga(null);
            this.popup.remove();
        });
        // Click an LGA polygon: select it (highlight + focus badge) and float its row to the
        // top of the exposure table. LGAs with no closure activity aren't in the table, so a
        // click on one is ignored.
        this.map.on("click", LGA_FILL_LAYER_ID, (e: any) => {
            const feature = e.features?.[0];
            if (!feature) return;
            const code = String(feature.properties?.code ?? feature.id ?? "");
            const row = this.rowByCode.get(code);
            if (!row || row.lga_code23 == null) return;
            this.selectLga(String(row.lga_code23), { fromMap: true });
        });
    }

    /**
     * Highlights the currently hovered LGA and clears the highlight from any
     * previously hovered LGA, ensuring only one area is highlighted at a time.
     */
    setHoveredLga(code: string | null) {
        if (code === this.hoveredCode) return;
        const featureState = (id: string, hover: boolean) =>
            this.map.setFeatureState(
                {
                    source: BOUNDARIES_SOURCE_ID,
                    sourceLayer: LGA_SOURCE_LAYER,
                    id,
                },
                { hover },
            );
        if (this.hoveredCode !== null) featureState(this.hoveredCode, false);
        this.hoveredCode = code;
        if (code !== null) featureState(code, true);
    }

    buildPopupHtml(feature: any, row: any): string {
        const name =
            row?.lga_name23 ?? feature.properties?.name ?? "Unknown LGA";
        const state = row?.ste_name21 ?? "";
        const metricLabel = this.currentMetric.label;
        const metricValue = row
            ? this.currentMetric.fmt(row[this.metric])
            : "No data in range";
        const basis = this.currentMetric.basis;
        return (
            `<div class="rca-lga-popup">` +
            `<div class="popup-name">${escapeHtml(name)}</div>` +
            (state
                ? `<div class="popup-state">${escapeHtml(state)}</div>`
                : "") +
            `<div class="popup-metric">` +
            `<span class="popup-metric-label">${escapeHtml(
                metricLabel,
            )}:</span> ` +
            `<span class="popup-metric-value">${escapeHtml(
                metricValue,
            )}</span>` +
            `</div>` +
            (basis
                ? `<div class="popup-basis">${escapeHtml(basis)}</div>`
                : "") +
            `</div>`
        );
    }

    fmtLegend(value: number): string {
        return this.currentMetric.fmt(value);
    }

    /** v-data-table row click. */
    onRowClick(arg1: any, arg2: any) {
        const item = arg1 && arg1.lga_code23 != null ? arg1 : arg2 && arg2.item;
        if (!item || item.lga_code23 == null) return;
        this.selectLga(String(item.lga_code23), { fromMap: false });
    }

    /**
     * Focus an LGA. Highlights it on the map and shows the focus badge.
     */
    selectLga(rawCode: string, opts: { fromMap: boolean }) {
        this.selectedLgaCode = rawCode;
        this.bringSelectedToTop = opts.fromMap;
        this.applyFocusHighlight();
        if (!opts.fromMap) {
            this.zoomToLga(BOUNDARY_LGA_CODE_PREFIX + rawCode);
        }
    }

    /** Point the focus-outline layer at the selected LGA (or hide it when nothing is
     * selected). */
    applyFocusHighlight() {
        if (!this.map || !this.map.getLayer(LGA_FOCUS_LAYER_ID)) return;
        const filterCode =
            this.selectedLgaCode != null
                ? BOUNDARY_LGA_CODE_PREFIX + this.selectedLgaCode
                : "__none__";
        this.map.setFilter(LGA_FOCUS_LAYER_ID, [
            "==",
            ["get", "code"],
            filterCode,
        ]);
    }

    /**
     * Fly the map to the focused LGA. Bounds come from the prefetched
     * ``lgaBoundsCache``.
     */
    zoomToLga(prefixedCode: string) {
        if (!this.map) return;
        const bounds = this.lgaBoundsCache.get(prefixedCode);
        if (bounds) {
            this.map.fitBounds(bounds, {
                padding: 48,
                maxZoom: 9,
                duration: 900,
            });
        }
    }

    /** Fetch every LGA's bounding box once and populate ``lgaBoundsCache``. The extents are
     * static, so this runs a single time (guarded on an already-populated cache). */
    fetchLgaBounds() {
        if (!this.datasetSlug || this.lgaBoundsCache.size) return;
        Vue.axios({
            url: endpoints.roadClosureAnalyticsLgaBoundsUrl(this.datasetSlug),
        })
            .then((response: any) => {
                const rows = Array.isArray(response.data) ? response.data : [];
                for (const r of rows) {
                    if (r.code == null) continue;
                    this.lgaBoundsCache.set(String(r.code), [
                        [Number(r.min_lng), Number(r.min_lat)],
                        [Number(r.max_lng), Number(r.max_lat)],
                    ] as [[number, number], [number, number]]);
                }
            })
            .catch((err: any) => {
                console.error("[RCA Geography lga_bounds]", err);
            });
    }

    /** Clear the focus — hide the badge + map outline and zoom back to the national view. */
    clearFocus() {
        this.selectedLgaCode = null;
        this.bringSelectedToTop = false;
        this.applyFocusHighlight();
        if (this.map) {
            this.map.flyTo({
                center: this.initialCenter,
                zoom: this.initialZoom,
                duration: 600,
            });
        }
    }

    /** Bold the selected LGA's row (v-data-table item-class hook). */
    rowClass(item: any): string {
        return this.selectedLgaCode != null &&
            String(item.lga_code23) === this.selectedLgaCode
            ? "selected-lga-row"
            : "";
    }

    /** The exposure row for the focused LGA, if it's in the loaded set. */
    get selectedRow(): any {
        if (this.selectedLgaCode == null) return null;
        return (
            this.lgaRows.find(
                (r) => String(r.lga_code23) === this.selectedLgaCode,
            ) ?? null
        );
    }

    /** Text for the focus badge: LGA name plus state when known. */
    get focusLabel(): string | null {
        const r = this.selectedRow;
        if (!r) return null;
        return r.ste_name21
            ? `${r.lga_name23} · ${r.ste_name21}`
            : r.lga_name23 || null;
    }

    get rowByCode(): Map<string, any> {
        // TEMPORARY: keyed on the prefixed form so the hover handler can look up by the
        // boundary tile's `code` directly. Drop the prefix when the prototype writes
        // LGA_<code> natively — see BOUNDARY_LGA_CODE_PREFIX above.
        const m = new Map<string, any>();
        for (const r of this.lgaRows) {
            if (r.lga_code23 != null) {
                m.set(BOUNDARY_LGA_CODE_PREFIX + String(r.lga_code23), r);
            }
        }
        return m;
    }

    get currentMetric(): MetricOption {
        return (
            METRIC_OPTIONS.find((m) => m.key === this.metric) ??
            METRIC_OPTIONS[0]
        );
    }

    /** Min / max of the chosen metric across visible LGAs — drives both the fill colour
     * scale and the legend. The min is the smallest *positive* value present in the
     * data, so the legend's left label reflects what the lightest coloured
     * polygon actually represents rather than always implying zero. Falls back to 0
     * when no positive values are found. */
    get metricRange(): { min: number; max: number } {
        let max = 0;
        let min = Infinity;
        for (const r of this.lgaRows) {
            const v = Number(r[this.metric]) || 0;
            if (v <= 0) continue;
            if (v > max) max = v;
            if (v < min) min = v;
        }
        return { min: Number.isFinite(min) ? min : 0, max };
    }

    lgaHeaders = [
        { text: "LGA", value: "lga_name23" },
        { text: "State", value: "ste_name21" },
        { text: "Selected days", value: "selected_day_count", align: "end" },
        {
            text: "Closure duration (days)",
            value: "active_day_count",
            align: "end",
        },
        {
            text: "Share of days closed",
            value: "active_day_share",
            align: "end",
        },
        {
            text: "Closure events",
            value: "closure_event_count_sum",
            align: "end",
        },
        {
            text: "Closed segment-days",
            value: "closed_link_count_sum",
            align: "end",
        },
        {
            text: "Closed-segment km-days",
            value: "closed_length_km_sum",
            align: "end",
        },
        {
            text: "Blocked tonnes",
            value: "blocked_exposed_tonnes",
            align: "end",
        },
        {
            text: "Rerouted tonnes",
            value: "rerouted_exposed_tonnes",
            align: "end",
        },
        {
            text: "Total extra transport kilometres *",
            value: "net_additional_travel_km",
            align: "end",
        },
        {
            text: "Total extra transport hours *",
            value: "net_additional_travel_hours",
            align: "end",
        },
        {
            text: "Blocked enterprise-days",
            value: "blocked_enterprise_day_count",
            align: "end",
        },
        {
            text: "Rerouted enterprise-days",
            value: "rerouted_enterprise_day_count",
            align: "end",
        },
    ].filter(
        (h) =>
            TONNES_TRAVEL_METRICS_ENABLED || !TONNES_TRAVEL_KEYS.has(h.value),
    );

    get lgaRows() {
        return this.lga.data;
    }

    // LGA search options — LGAs with exposure data, sorted by name.
    get lgaOptions(): { text: string; value: string }[] {
        return [...this.lgaRows]
            .filter((r) => r.lga_code23 != null)
            .map((r) => ({
                text: r.ste_name21
                    ? `${r.lga_name23} · ${r.ste_name21}`
                    : String(r.lga_name23),
                value: String(r.lga_code23),
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }

    // Search v-model: reflects map/table clicks; on pick → focus + zoom; on clear → reset.
    get lgaSelection(): string | null {
        return this.selectedLgaCode;
    }
    set lgaSelection(code: string | null) {
        if (code == null) {
            this.clearFocus();
            return;
        }
        const raw = String(code);
        this.selectLga(raw, { fromMap: true });
        this.zoomToLga(BOUNDARY_LGA_CODE_PREFIX + raw);
    }

    /**
     * The LGA exposure data table shows EVERY loaded LGA — uncapped.
     */
    get tableRows(): any[] {
        const base = this.lgaRows;
        // When the selection came from a map click, surface that LGA's row at the top.
        if (!this.bringSelectedToTop || this.selectedLgaCode == null) {
            return base;
        }
        const sel = this.selectedRow;
        if (!sel) return base;
        const rest = base.filter(
            (r) => String(r.lga_code23) !== this.selectedLgaCode,
        );
        return [sel, ...rest];
    }
}
